import { Component, computed, effect, ElementRef, inject, Input, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CkRegisterService } from '../../ck-register/ck-register.service';
import { RecordService } from '../../record/record.service';
import { AppToastsService } from '../../shared/app-toasts/app-toasts.service';
import { DataMessageService } from '../../shared/data-message.service';
import { ExportExcelService } from '../../shared/export-excel.service';
import { IRptEspCob, IRptEspCobClm, IRptEspCobIns, IRptEspCobParm, IRptEspCobPay, IRptEspCobDet, IRptEspCobTot, IRptEspCobView } from '../../shared/interfaces/rptEspCob';
import { ModalService } from '../../shared/modal.service';
import { take } from 'rxjs';
import moment from 'moment';
import { ChangeDetectorRef, NgZone } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { RecordCaseModalComponent } from '../../shared/record-case-modal/record-case-modal.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

interface IExpandedCol {
  index: number;
  show: boolean;
  claims: IRptEspCobClm[];
}

type PrintableIns = IRptEspCobIns & {
  break?: number;
  Cnt?: string;
};
type PrintableClm = IRptEspCobClm & {
  break?: number;
  pgCnt?: number;
};
type PrintableDet = IRptEspCobDet & {
  break?: number;
  pgCnt?: number;
};
type PrintablePay = IRptEspCobPay & {
  break?: number;
  pgCnt?: number;
};

// Huge data hacks

type Indexes = {
  claimsByIns: Map<string, IRptEspCobClm[]>;
  detByCas: Map<string, IRptEspCobDet[]>;
  payByCas: Map<string, IRptEspCobPay[]>;
};

let _idx: Indexes | undefined;

export function buildIndexes(i: IRptEspCob): Indexes {
  const claimsByIns = new Map<string, IRptEspCobClm[]>();
  for (const c of i.clm) {
    (claimsByIns.get(c.casInID) ?? claimsByIns.set(c.casInID, []).get(c.casInID)!).push(c);
  }

  const detByCas = new Map<string, IRptEspCobDet[]>();
  for (const det of i.det) {
    const keyD = det.keyDetCasID.split('_').pop()!; // faster than endsWith-find combo
    (detByCas.get(keyD) ?? detByCas.set(keyD, []).get(keyD)!).push(det);
  }

  const payByCas = new Map<string, IRptEspCobPay[]>();
  for (const pay of i.pay) {
    const keyP = pay.keyPayCasID.split('_').pop()!;
    (payByCas.get(keyP) ?? payByCas.set(keyP, []).get(keyP)!).push(pay);
  }
  return { claimsByIns, detByCas, payByCas };
}

export function buildJoinRequest(all: AllData) {
  // --- helpers ---
  const sliceSizeIns = all.ins.length;
  const sliceSizeRest = 1;
  const makeData = (insSlice: any[] = [], clmSlice: any[] = [], detSlice: any[] = [], paySlice: any[] = []) => ({
    ...all,
    ins: insSlice,
    clm: clmSlice,
    det: detSlice,
    pay: paySlice
  });

  // If you already import lodash.chunk, keep using that; otherwise a tiny fallback:
  const chunkLocal = <T>(arr: T[], size: number): T[][] => {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };
  const chunkFn = (typeof (globalThis as any).chunk === 'function' ? (globalThis as any).chunk : chunkLocal);

  // --- 1) split INS into 500s ---
  const insChunks = chunkFn(all.ins, sliceSizeIns);
  if (!insChunks.length) throw new Error('No data');

  // Base payload (first chunk of INS)
  const first =
    // if your buildChunkData filters clm/det/pay to the ins slice, keep using it:
    typeof buildChunkData === 'function'
      ? buildChunkData(all, insChunks[0])
      : makeData(insChunks[0], all.clm, all.det, all.pay);

  const ops: Array<any> = [];

  // Remaining INS chunks (append)
  for (let i = 1; i < insChunks.length; i++) {
    ops.push({
      type: 'append',
      template: { name: 'ins-html' },
      data: (typeof buildChunkData === 'function')
        ? buildChunkData(all, insChunks[i])
        : makeData(insChunks[i], all.clm, all.det, all.pay),
      mergeWholeDocument: true
    });
  }

  // --- 2) split CLM into sliceSizeRest and append ---
  for (let c = 0; c < all.clm.length; c += sliceSizeRest) {
    const clmSlice = all.clm.slice(c, c + sliceSizeRest);
    ops.push({
      type: 'append',
      template: { name: 'ins-html' },
      // if template supports "claims-only" pages:
      data: makeData([], clmSlice, [], []),
      mergeWholeDocument: true
    });
  }

  // --- 3) split DET into sliceSizeRest and append ---
  for (let d = 0; d < all.det.length; d += sliceSizeRest) {
    const detSlice = all.det.slice(d, d + sliceSizeRest);
    ops.push({
      type: 'append',
      template: { name: 'ins-html' },
      // if template supports "details-only" pages:
      data: makeData([], [], detSlice, []),
      mergeWholeDocument: true
    });
  }

  // --- 4) split PAY into sliceSizeRest and append ---
  for (let p = 0; p < all.pay.length; p += sliceSizeRest) {
    const paySlice = all.pay.slice(p, p + sliceSizeRest);
    ops.push({
      type: 'append',
      template: { name: 'ins-html' },
      // if template supports "payments-only" pages:
      data: makeData([], [], [], paySlice),
      mergeWholeDocument: true
    });
  }

  // --- final request ---
  return {
    template: {
      name: 'ins-html',
      recipe: 'chrome-pdf',
      engine: 'handlebars',
      options: { reports: { save: true } },
      chrome: {
        printBackground: true,
        waitForNetworkIdle: false
      }
    },
    data: first,
    pdfOperations: ops  // append rest of chunked data
  };
}

export function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export type AllData = {
  head: any;
  ins: any[]; clm: any[]; det: any[]; pay: any[];
  [k: string]: any;
};

export function buildChunkData(all: AllData, insSlice: any[]): AllData {
  const inIDs = new Set(insSlice.map(i => i.InID));

  let clm: any[] = all.clm.filter(c => inIDs.has(c.casInID));
  const casIDs = new Set(clm.map(c => c.keyCasID));

  // Your own matching rules (taken from your template code)
  const det = all.det.filter(d => casIDs.has(d.keyDetCasID));
  const pay = all.pay.filter(p => casIDs.has(p.keyPayCasID));

  return { ...all, ins: insSlice, clm, det, pay };
}

@Component({
  selector: 'app-rpt-esp-cob',
  imports: [],
  templateUrl: './rpt-esp-cob.component.html',
  styleUrl: './rpt-esp-cob.component.css',
  standalone: false
})
export class RptEspCobComponent {
  _recordService = inject(RecordService);
  _cd = inject(ChangeDetectorRef);
  _ngZone = inject(NgZone);
  _ngbModal = inject(NgbModal);

  @Input() rptEspCobData!: WritableSignal<IRptEspCob>;
  @Input() rptFromDt: WritableSignal<string> | undefined;
  @Input() rptToDt: WritableSignal<string> | undefined;
  @Input() rptOptions: WritableSignal<any[]> | undefined;
  @Input() seldProvsIDs: WritableSignal<string> | undefined;
  @Input() seldInses1IDs: WritableSignal<string> | undefined;
  @Input() seldInses2IDs: WritableSignal<string> | undefined;
  @Input() seldFacsIDs: WritableSignal<string> | undefined;
  @Input() seldLocalesNo: WritableSignal<string> | undefined;
  @Input() sioQuery!: WritableSignal<string | null>;
  @Input() espCobTot: WritableSignal<string> | undefined;
  @Input() spinner: WritableSignal<boolean> | undefined;

  @ViewChild(MatSort) sort: MatSort | undefined;
  @ViewChild('vp', { static: false }) vp!: CdkVirtualScrollViewport;

  readonly ROW_SIZE = 33; // 32px content + 1px border (see CSS)
  maxVpHeight = 500; // Max height of the viewport in pixels
  expandedCounts = new Map<string, number>();
  hoverIndex: number | null = null;
  hoveredItem: any | null = null;

  sn!: string;
  engLang!: boolean;
  userID!: string;
  userLastNm!: string;
  userFirstNm!: string;

  modalStack: string[] = [];

  activeView: string = ''; // Holds the currently selected view name
  insurance: IRptEspCobIns[] = [];
  claim: IRptEspCobClm[] = [];
  detail: IRptEspCobDet[] = [];
  payment: IRptEspCobPay[] = [];
  showClaims: IExpandedCol[] = []; // Array to track which rows have details expanded
  insPrintAll: boolean = false; // For printing all ins,clm,det,pay as displayed; WARNING: can yield a memory (to much data) error in JSReports
  insPrintAllInsOnly: boolean = false; // For printing all only insurances

  dataSource!: MatTableDataSource<IRptEspCobIns>;
  rptEspCobDisplydCols: string[] = [
    'InLNm',
    'InID',
    'Usual',
    'Xpected',
    'AdjPos',
    'Payed',
    'AdjNeg',
    'IBal',
    'Pvdo',
    'PPayed',
    'PDisc',
    'PBal',
    'Cnt',
    'printr'
  ];

  readonly views = computed<IRptEspCobView[]>(() => {
    const arr = this.rptEspCobData().views;
    console.log('views computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  readonly parm = computed<IRptEspCobParm[]>(() => {
    const arr = this.rptEspCobData().parm;
    console.log('parm computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  readonly ins = computed<IRptEspCobIns[]>(() => {
    const arr = this.rptEspCobData().ins;
    console.log('det computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  readonly clm = computed<IRptEspCobClm[]>(() => {
    const arr = this.rptEspCobData().clm;
    console.log('clm computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  readonly det = computed<IRptEspCobDet[]>(() => {
    const arr = this.rptEspCobData().det;
    console.log('pay computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  readonly pay = computed<IRptEspCobPay[]>(() => {
    const arr = this.rptEspCobData().pay;
    console.log('pay computed called', arr.length);
    if (!arr.length) return [];
    return arr.slice();
  });

  readonly tot = computed<IRptEspCobTot>(() => {
    const obj = this.rptEspCobData().tot;
    console.log('pay computed called', obj);
    if (!obj) {
      // Return a default object matching IRptEspCobTot structure
      return {
        InID: '',
        InLNm: '',
        shoInID: '',
        Usual: '0',
        Xpected: '0',
        AdjPos: '0',
        Payed: '0',
        AdjNeg: '0',
        IBal: '0',
        Pvdo: '0',
        PPayed: '0',
        PDisc: '0',
        PBal: '0',
        Cnt: '0'
      };
    }
    return obj;
  });

  // EFFECT: call `det()` so changes update dataSource
  // This is declared as a class level field so Angular’s DI is available.
  private readonly _updateData = effect(() => {
    const varr = this.views(); // unwrap computed → views, causes computed to be called
    const parr = this.parm(); // unwrap computed → parm, causes computed to be called
    const iarr = this.ins();  // unwrap computed → darr, causes computed to be called
    const carr = this.clm();  // unwrap computed → carr, causes computed to be called
    const darr = this.det();  // unwrap computed → carr, causes computed to be called
    const pyarr = this.pay(); // unwrap computed → pyarr, causes computed to be called
    const tarr = this.tot();  // unwrap computed → tot, causes computed to be called
    this.dataSource = new MatTableDataSource(iarr);  // side‐effect: push into MatTableDataSource
    this.dataSource.sort = this.sort ?? null; // Always follow this.dataSource = ... with this to maintain header sort arrow synced
  });

  constructor(private _dataMessageService: DataMessageService,
    private _modalService: ModalService,
    private _appToastsService: AppToastsService,
    private _ckRegisterService: CkRegisterService,
    private _xcel: ExportExcelService,
    public _dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this._dataMessageService.currentSnStr.subscribe(snStr => this.snChange(snStr)); // Subscription looking for changes in sn
    this._dataMessageService.currentEngLangStr.subscribe(engLangStr => this.engLangChange(engLangStr)); // Subscription looking for changes in engLang
  }

  ngAfterViewInit() {
  }

  syncHeaderPad(vp: CdkVirtualScrollViewport) {
    const el = vp.elementRef.nativeElement as HTMLElement;
    const pad = el.offsetWidth - el.clientWidth; // scrollbar width
    const header = document.querySelector<HTMLElement>('#vtHeaderEl');
    if (header) header.style.paddingRight = `${pad}px`;
  }

  snChange(snStr: string) { // for when sn changes
    this.sn = snStr;
  }

  engLangChange(engLangStr: string) { // for when language changes
    engLangStr === 'true' ? this.engLang = true : this.engLang = false;
  }

  engEspCobDescr(vname: string): string {
    if (this.engLang) {
      const map: { [key: string]: string } = {
        "Esp vs Cob:": "Exp vs Inc:",
        " a ": " to ",
        " pedido ": " requested "
      };
      const re = /Esp vs Cob:| a | pedido /g;
      return vname.replace(re, m => map[m]);
    }
    return vname; // Spanish version
  }

  onChange_view(event: string) {  // Loads up an existing view in event
    this.activeView = event; // Set the active view to the selected one
    this.rptEspCobData.update(current => ({
      views: current.views,
      parm: [],
      ins: [],
      clm: [],
      det: [],
      pay: [],
      tot: {
        InID: '',
        InLNm: 'Total',
        shoInID: '',
        Usual: '0',
        Xpected: '0',
        AdjPos: '0',
        Payed: '0',
        AdjNeg: '0',
        IBal: '0',
        Pvdo: '0',
        PPayed: '0',
        PDisc: '0',
        PBal: '0',
        Cnt: '0'
      }
    }));
    this.espCobTot?.set(''); // Reset dtxTot when changing views (guard when espCobTot is undefined)
    this.showClaims = []; // Reset showClaims array when changing views

    const qry = "EXEC dbo.spRptDtx @viewNm = '" + this.activeView + "';";
    window.scroll(0, 0);
    console.log('%c' + 'query ' + qry, 'color: green; background: yellow; font-size: 12px');
    this.sioQuery.set(qry);
  }

  private waitForPaint(): Promise<void> { // Paint the spinner
    return new Promise<void>((resolve) => {
      requestAnimationFrame((_t1) => {
        requestAnimationFrame((_t2) => resolve());
      });
    });
  }

  // private async waitForPaint() {
  //   // rAF callback runs just before paint; schedule another turn to let the paint happen
  //   await new Promise<void>(r => requestAnimationFrame(() => r()));
  //   await new Promise<void>(r => setTimeout(r, 0)); // allow the frame to actually paint
  // }

  async onClick_expandIns(ins: IRptEspCobIns, event?: MouseEvent) {
    if ((event?.target as HTMLElement)?.innerHTML === 'print') return;

    const current = this.rptEspCobData(); if (!current) return;
    const idxIns = current.ins.findIndex(i => i.InID === ins.InID);

    current.ins[idxIns].printing = true;
    this._cd.detectChanges();
    await this.waitForPaint(); // spinner shows

    try {
      const result = await this._ngZone.runOutsideAngular(async () => {
        if (!_idx) _idx = buildIndexes(current);

        // Collapse if already open
        if (this.showClaims[idxIns]?.show) return {
          collapse: true,
          claims: [] as IRptEspCobClm[],
          det: [] as IRptEspCobDet[],
          pay: [] as IRptEspCobPay[]
        };

        const claims = _idx.claimsByIns.get(ins.InID) ?? [];
        // Build detail/pay in chunks so we never block for long
        const det: IRptEspCobDet[] = [];
        const pay: IRptEspCobPay[] = [];
        let i = 0;
        for (const c of claims) {
          const cas = c.keyCasID;
          const d = _idx.detByCas.get(cas); if (d) det.push(...d);
          const p = _idx.payByCas.get(cas); if (p) pay.push(...p);
          if ((++i % 1500) === 0) await new Promise(r => setTimeout(r, 0)); // yield
        }

        let cl = (claims.length ?? 0) + ((claims.length ?? 0) > 0 ? 1 : 0),
          dl = (det.length ?? 0),
          pl = (pay.length ?? 0);

        let di = new Map<string, number>();
        let pi = new Map<string, number>();

        for (const d of det) { // Account for subheadings, 1 per case
          dl += (di.get(d.keyDetCasID) ?? di.set(d.keyDetCasID, 1).get(d.keyDetCasID)!);
        }
        for (const p of pay) { // Account for subheadings, 1 per case
          pl += (di.get(p.keyPayCasID) ?? pi.set(p.keyPayCasID, 1).get(p.keyPayCasID)!);
        }

        this.expandedCounts.set(ins.InID, cl + dl + pl);
        return { collapse: false, claims, det, pay };
      });

      this._ngZone.run(() => {
        if (result.collapse) {
          this.showClaims[idxIns] = { index: idxIns, show: false, claims: [] };
          current.ins[idxIns].expanded = 0;
        } else {
          this.claim = result.claims;
          this.detail = result.det;
          this.payment = result.pay;
          this.showClaims[idxIns] = { index: idxIns, show: true, claims: result.claims };
          current.ins[idxIns].expanded = 1;
        }
        this._cd.markForCheck();
      });
    } finally {
      this._ngZone.run(() => {
        current.ins[idxIns].printing = false;
        this._cd.markForCheck();
      });
    }
  }

  trackExpanded = (_: number, it: any) =>
    it.kind === 'clm' ? 'c' + it.c.CasID :
      it.kind === 'det' ? 'd' + it.d.keyDetID_CasID :
        it.kind === 'pay' ? 'p' + it.p.keyPayDetID_PayCasID : _;

  getExpandedItems(row: any) {
    // Use your pre-indexed Maps for O(n) lookups if possible.
    if (!_idx) return [];
    const clms = _idx.claimsByIns.get(row.InID) ?? [];   // IRptEspCobClm[]
    const items: Array<{ kind: 'clm' | 'det' | 'pay'; c?: any; d?: any; p?: any }> = [];

    for (const c of clms) {
      items.push({ kind: 'clm', c });

      const dets = _idx.detByCas.get(c.keyCasID) ?? [];   // IRptEspCobDet[]
      for (const d of dets) items.push({ kind: 'det', d });

      const pays = _idx.payByCas.get(c.keyCasID) ?? [];  // IRptEspCobPay[]
      for (const p of pays) items.push({ kind: 'pay', p });
    }
    return items;
  }

  isExpanded(row: IRptEspCobIns): boolean {  // Call from expanded row in template
    if (!row) return false; // If row is undefined or null, return false

    const current = this.rptEspCobData(); // Get the current rptDtxData ins
    if (!current) return false; // Get out if no data

    const idx = current.ins.findIndex(ins => ins.InID === row?.InID); // Current ins row index

    this.claim = this.showClaims[idx]?.claims || [];
    return this.showClaims[idx]?.show;  // Set to true or false based on the expanded state
  }

  onRowHover(item: any, index: number, over: boolean) {
    this.hoverIndex = over ? index : null;
    this.hoveredItem = over ? item : null;
  }

  open(content: any, opts: any, modalNm: string) {
    this.modalStack.push(modalNm);
    const modal$ = this._modalService.open(content, opts);
    modal$.subscribe({
      next: (result) => {    // Save, Ok, etc
        console.log('result', result);
        const qry = "EXEC dbo.spRptDtx @viewNm = '" + this.activeView + "';";
        window.scroll(0, 0);
        console.log('%c' + 'query ' + qry, 'color: green; background: yellow; font-size: 12px');
        this.sioQuery.set(qry);
        this.modalStack.pop();
      },
      error: (reason) => {    // dismiss, X, Close, Esc
        console.log('reason', reason);
        console.log('modalStack', this.modalStack);
        switch (this.modalStack[this.modalStack.length - 1]) {
          case 'recordCaseModal':
            // Do some cleanup
            break;
          default:
        }
        this.modalStack.pop();
      }
    });
  }

  onClick_menu(event: { target: { id: any; }; }) {
    if (this.spinner?.()) {
      this._appToastsService.updateDeadCenter(true); // Show toast in dead center of viewport
      this._appToastsService.show(this.engLang ? 'Wait for the current task to finish' : 'Espere que la tarea activa termine', { header: this.engLang ? 'Busy' : 'Ocupado', autohide: true, warning: true });
      return; // Get out if spinner is already set to true     
    }

    switch (event.target.id) {
      case 'expandRowsDown':
        this.rptEspCobData().ins.forEach((ins) => {
          if (!this.isExpanded(ins)) {
            this.onClick_expandIns(ins);
          }
        });
        break;
      case 'collapseRowsUp':
        this.rptEspCobData().ins.forEach((ins) => {
          if (this.isExpanded(ins)) {
            this.onClick_expandIns(ins);
          }
        });
        break;
      case 'print':
        debugger;
        this.printRpt(null, false); // Print all insurances
        break;
      case 'delete':
        this.deleteRpt();
        break;
      default:
    }
  }

  truncateString(str: string, len: number): string {
    if (str === '(0)') return ''; // If the string is '(0)', supress it
    // Truncate the string to the specified length and add ellipsis if necessary
    return str.length > len ? str.substring(0, 10) + '...' + (str.substring(0, 10).indexOf(str.substring(str.length - 7)) > -1 ? '' : str.substring(str.length - 7)) : str;
  }

  printRpt(pIns: IRptEspCobIns | null, excel: boolean) {
    let ins2Prt: PrintableIns[] = [];
    let clm2Prt: PrintableClm[] = [];
    let det2Prt: PrintableDet[] = [];
    let pay2Prt: PrintablePay[] = [];
    let tot2Prt: IRptEspCobTot = {
      InID: '',
      InLNm: 'Total',
      shoInID: '',
      Usual: '0',
      Xpected: '0',
      AdjPos: '0',
      Payed: '0',
      AdjNeg: '0',
      IBal: '0',
      Pvdo: '0',
      PPayed: '0',
      PDisc: '0',
      PBal: '0',
      Cnt: '0'
    };
    let lc = 0; // Line Count 
    let pgCnt = 1;  // Page number
    let pxPg = 1010 - 90; // y pixels per page - 1st header & footer
    let pxCnt = 0; // Pixel count first page
    let idxC = 0;  // To detect sub headings clm
    let idxD = 0;  // To detect sub headings det
    let idxP = 0;  // To detect sub headings pay

    const payDupProps2Delete = ['PayID', 'PayDetID', 'PayPS', 'PayDt', 'PayAmnt', 'PayCkNo', 'PayApCode', 'PayMode', 'PayMemo', 'sqlProcNm', 'expandCol'];
    const detDupProps2Delete = ['DetCasID', 'DetPS', 'oDetPS', 'dtFrom', 'DetQty', 'DetPos', 'DetPcode', 'mods', 'DetUsual', 'DetXpect', 'DetPvdo', 'prod', 'DetDesc', 'DetDx', 'sqlProcNm', 'expandCol'];

    if (pIns) { // Printing one insurance and details
      ins2Prt.push(pIns);
    } else { // Printing all insurances
      ins2Prt = this.ins().slice();
    }
    if (!ins2Prt.length) {
      this._appToastsService.show(this.engLang ? 'Nothing to print' : 'Nada que imprimir', { header: this.engLang ? 'No Data' : 'Sin Datos', autohide: true, warning: true });
      this.spinnersOff();
      return; // Get out if no data
    }

    ins2Prt.forEach(ins => {
      lc++;
      pxCnt += 25; // Approx height of one line in pixels
      ins['Cnt'] = pgCnt.toString();
      if (pxCnt >= pxPg) {
        ins['break'] = 1; // Sets a page break
        pgCnt++;
        pxCnt = 0; // Reset
        lc = 0; // Reset
        pxPg = 1010;
      } else {
        ins['break'] = 0;
      }

      if (!this.insPrintAllInsOnly) {
        clm2Prt.push(...this.clm().filter(c => ins.expanded === 1 && c.casInID === ins.InID));

        idxC = 0;
        clm2Prt.forEach(clm => {
          clm.CasOfNo = clm.CasOfNo.replace(/^0*/, ''); // Remove leading zeros
          det2Prt.push(...this.det().filter(det => ins.expanded === 1 && clm.casInID === ins.InID && det.keyDetCasID === clm.keyCasID));
          pay2Prt.push(...this.pay().filter(pay => ins.expanded === 1 && clm.casInID === ins.InID && pay.keyPayCasID === clm.keyCasID));

          if (clm.casInID === ins.InID) {
            lc++;
            if (idxC === 0) {
              pxCnt += 53; // Approx height of first line in pixels including heading
            } else {
              pxCnt += 45; // Approx height of lines from idxC = 1 in pixels
            }
            idxC++;

            clm['pgCnt'] = pgCnt;
            if (pxCnt >= pxPg) {
              clm['break'] = 1; // Sets a page break
              pgCnt++;
              pxCnt = 0; // Reset
              lc = 0; // Reset
              pxPg = 1010;
            } else {
              clm['break'] = 0;
            }

            idxD = 0;
            det2Prt.forEach(det => {
              if (det.keyDetCasID === clm.keyCasID) {
                lc++;
                if (idxD === 0) {
                  pxCnt += 30; // Approx height of first line in pixels including heading
                } else {
                  pxCnt += 22; // Approx height of lines from idxD = 1 in pixels
                }
                idxD++;

                det['pgCnt'] = pgCnt;
                if (pxCnt >= pxPg) {
                  det['break'] = 1; // Sets a page break
                  pgCnt++;
                  pxCnt = 0; // Reset
                  lc = 0; // Reset
                  pxPg = 1010;
                } else {
                  det['break'] = 0;
                }
              }
            });

            idxP = 0;
            pay2Prt.forEach(pay => {
              if (pay.keyPayCasID === clm.keyCasID) {
                lc++;
                if (idxP === 0) {
                  pxCnt += 29; // Approx height of first line in pixels including heading
                } else {
                  pxCnt += 21; // Approx height of lines from idxP = 1 in pixels
                }
                idxP++;

                pay['pgCnt'] = pgCnt;
                if (pxCnt >= pxPg) {
                  pay['break'] = 1;
                  pgCnt++;
                  pxCnt = 0; // Reset
                  lc = 0; // Reset
                  pxPg = 1010;
                } else {
                  pay['break'] = 0;
                }
              }

            });
          }
        });
      }
    });

    if (pIns) { // If printing one insurance, set the total for that insurance
      tot2Prt = { ...pIns };
      tot2Prt.InLNm = 'Total'; // Set the total insurance name
    } else { // If printing all insurances, calculate the total
      tot2Prt = { ...this.tot() };
    }

    let data: any = {
      "head": {
        "site": {
          "nm": this._recordService.site.nm,
          "ad1": this._recordService.site.ad1,
          "ad2": this._recordService.site.ad2,
          "ct": this._recordService.site.ct,
          "st": this._recordService.site.st,
          "zp": this._recordService.site.zp,
          "tl1": this._recordService.site.tl1,
          "xt1": this._recordService.site.xt1,
          "tl2": this._recordService.site.tl2,
          "xt2": this._recordService.site.xt2
        },
        "sn": this._recordService.sn,
        "dt": moment(new Date()).format('MM/DD/YY h:mm a'),
        "param": {
          "fromDt": this.rptFromDt?.() ?? '',
          "toDt": this.rptToDt?.() ?? '',
          "provs": this.seldProvsIDs?.() ?? '',
          "facs": this.seldFacsIDs?.() ?? '',
          "locales": this.seldLocalesNo?.() ?? '',
          "plans1": pIns ? pIns.InID : (this.seldInses1IDs?.() ?? ''),
          "plans2": pIns ? pIns.InID : (this.seldInses2IDs?.() ?? '')
        },
        "opt": this.rptOptions?.() ?? []
      },
      "ins": ins2Prt,
      "clm": clm2Prt,
      "det": det2Prt,
      "pay": pay2Prt,
      "tot": tot2Prt,
      "lastPg": pgCnt,
      "engLang": this.engLang
    };

    console.log('data', data);
    //        detDupProps2Delete.forEach(prop => { delete p[prop]; });  // Get rid of unneeded properties

    this.buildRpt(data); // Call the buildRpt method to prepare the report in JSReports
  }

  renderBlob(blob: Blob) {
    const url = window.URL.createObjectURL(blob);
    const newTab = window.open(url);

    if (newTab) {
      newTab.onload = () => {
        window.URL.revokeObjectURL(url);
      };
    } else {
      this._appToastsService.updateDeadCenter(false); // Show toast in dead center of viewport - property sent back to app.component via @ViewChild in app.component
      this._appToastsService.show('Failed to open new tab in browser.', { header: 'Error preparing report * Error preparando reporte', autohide: false, error: true });
    }
    this.spinnersOff();
  }

  spinnersOff() {
    this.spinner?.set(false);
    if (this.insPrintAll || this.insPrintAllInsOnly) {
      this.insPrintAll = false; // Reset insPrintAll after printing all insurances
      this.insPrintAllInsOnly = false; // Reset 
    } else {
      this.ins().forEach(ins => {
        ins.printing = false; // Reset printing state for each insurance after printing
      });
    }
  }

  backFromServerError(error: { message: string | TemplateRef<any>; }): void {
    this.spinnersOff();
    this._appToastsService.updateDeadCenter(false); // Show toast in dead center of viewport - property sent back to app.component via @ViewChild in app.component
    this._appToastsService.show(error.message, { header: 'Error preparing report * Error preparando reporte', autohide: false, error: true });
  }

  buildRpt(data: any) {
    // const bdy = {
    //   "template":
    //   {
    //     "name": "/rpts/esp-cob/ins/ins-html"
    //   }, "data": data,
    //   "options": { "reports": { "save": true } }
    // };

    console.log('printData', data);

    let bdy = buildJoinRequest(data);

    this._ckRegisterService.postRenderJsRpt(bdy).subscribe({
      next: blob => { this.renderBlob(blob) },
      error: (error: any) => { this.backFromServerError(error) }
    });
  }

  deleteRpt() {
    if (!this.rptEspCobData().views.find(v => v.name === this.activeView)) { // Probably already deleted
      this.activeView = '';
    }

    if (!this.activeView) {
      this._appToastsService.updateDeadCenter(true); // Show toast in dead center of viewport
      this._appToastsService.show(this.engLang ? 'Select the desired report to delete' : 'Seleccione el reporte deseado para eliminar', { header: this.engLang ? 'Select report' : 'Seleccione reporte', autohide: true, warning: true });
      return;
    }

    // Prompt to delete
    this._modalService.confirm(this.engLang ? 'Delete selected report?' : '¿Eliminar el reporte seleccionado?'
      , this.engLang ? 'Confirm' : 'Confirme'
      , true
      , true
      , 'Ok'
      , 'Cancel')
      .pipe(
        take(1) // take() manages unsubscription for us
      ).subscribe(response => {
        if (response) { // response = undefined if Esc was pressed, False if closed with upper rt hand x
          this.spinner?.set(true);
          const qry = "EXEC dbo.spRptDtxDelete @viewNm = '" + this.activeView + "';";
          window.scroll(0, 0);
          console.log('%c' + 'query ' + qry, 'color: green; background: yellow; font-size: 12px');
          this.sioQuery.set(qry);
        }
      });
  }

  openRecordCaseModal(patNm: string, casNo: string, engLang: boolean, xtPID: number, xtCID: number) {
    const modalRef: NgbModalRef = this._ngbModal.open(RecordCaseModalComponent, { backdrop: 'static', size: 'lg', scrollable: true });
    Object.assign(modalRef.componentInstance, {
      patNmModalHd: patNm,
      casNoModalHd: casNo,
      engLang: engLang,
      xtPID: xtPID,
      xtCID: xtCID
    });
    modalRef.result.then( // Callback for when the modal is closed
      result => console.log('Got back', result),
      reason => console.log('Dismissed', reason)
    );
  }

  sortea(sort: { active: string; direction: string; }) {
    const arr2sort: any[] = this.ins().slice();

    if (sort.active === 'InLNm' || sort.active === 'IBal' || sort.active === 'AdjPos' || sort.active === 'AdjNeg' || sort.active === 'Xpected') {
      if (sort.direction === 'asc') {
        arr2sort.sort((a, b) => a[sort.active] < b[sort.active] ? 1 : -1);
      } else {
        arr2sort.sort((a, b) => a[sort.active] > b[sort.active] ? 1 : -1);
      }
    }

    this.dataSource = new MatTableDataSource(arr2sort.slice());
    this.dataSource.sort = this.sort ?? null; // Always follow this.dataSource = ... with this to maintain header sort arrow synced
  }

}


